/**
 * FAB (Floating Action Button) position configuration
 * Allows per-page customization of FAB positioning to avoid overlapping elements
 */

// Base spacing (matches header gap-4 = 16px)
export const FAB_BASE_OFFSET = 16;

// Extra margin when avoiding elements
export const FAB_ELEMENT_MARGIN = 16;

/**
 * Page-specific FAB configuration
 * Each config defines extra offset for that page type
 */
export type FabPageConfig = {
  /** Extra bottom offset for fixed elements on the page */
  extraOffset: number;
  /** Selector for fixed bottom element to avoid (if any) */
  fixedElementSelector?: string;
  /** Pattern to match pathname */
  pattern: RegExp | string;
};

/** Default config for unmatched pages */
const DEFAULT_CONFIG: FabPageConfig = {
  extraOffset: 0,
  pattern: /.*/,
};

/**
 * Page configurations - order matters (first match wins)
 */
export const FAB_PAGE_CONFIGS: FabPageConfig[] = [
  {
    // Tutorial pages have a sticky CTA bar at bottom (76px height)
    extraOffset: 76,
    fixedElementSelector: "[data-tutorial-cta]",
    pattern: /\/tutorials\/[^/]+$/,
  },
  {
    // Tutorial listing page - standard offset
    extraOffset: 0,
    pattern: /\/tutorials\/?$/,
  },
  {
    // Blog post pages - standard offset
    extraOffset: 0,
    pattern: /\/blog\/[^/]+$/,
  },
  {
    // Contact page - standard offset
    extraOffset: 0,
    pattern: /\/contact\/?$/,
  },
  {
    // Home page - standard offset
    extraOffset: 0,
    pattern: /^\/[a-z]{2}\/?$/,
  },
];

/**
 * Get FAB configuration for a given pathname
 */
export function getFabConfig(pathname: string): FabPageConfig {
  const matchConfig = (config: FabPageConfig): boolean =>
    typeof config.pattern === "string"
      ? pathname.includes(config.pattern)
      : config.pattern.test(pathname);

  return FAB_PAGE_CONFIGS.find(matchConfig) ?? DEFAULT_CONFIG;
}

/**
 * Get base bottom offset for FAB (without footer)
 * Combines base offset and page-specific offset
 */
export function getBaseFabOffset(pathname: string): number {
  const config = getFabConfig(pathname);
  return FAB_BASE_OFFSET + config.extraOffset;
}

/**
 * Calculate total bottom offset for FAB
 * Combines base offset, page-specific offset, and footer offset
 */
export function calculateFabBottomOffset(
  pathname: string,
  footerOffset: number,
): number {
  return getBaseFabOffset(pathname) + footerOffset;
}
